import { Action, Column, ColumnKeys, GridViews, ResponseGridWebComponentSchema, Source, Status, Type } from '../types';
import { BaseWebComponentDataProvider } from '../../../framework/BaseWebComponentDataProvider';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { ViewType } from '../../../grid/gridView/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';

export class ResponseViewSettingsDataProvider extends BaseWebComponentDataProvider {
  private getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: this.dependencies.translateFn('Type'),
        type: ColumnType.ENUM,
        values: [
          {
            label: this.dependencies.translateFn('Containment'),
            value: Type.CONTAINMENT,
          },
          {
            label: this.dependencies.translateFn('Mitigation'),
            value: Type.MITIGATION,
          },
          {
            label: this.dependencies.translateFn('Remediation'),
            value: Type.REMEDIATION,
          },
          {
            label: this.dependencies.translateFn('Hardening'),
            value: Type.HARDENING,
          },
        ],
        removable: true,
        flexGrow: 1,
      },
      {
        column: Column.ACTION,
        key: ColumnKeys.ACTION,
        columnName: this.dependencies.translateFn('Action'),
        type: ColumnType.ENUM,
        values: [
          {
            label: this.dependencies.translateFn('Isolate'),
            value: Action.ISOLATE,
          },
          {
            label: this.dependencies.translateFn('Delete email - O365'),
            value: Action.DELETE_EMAIL_O365,
          },
          {
            label: this.dependencies.translateFn('Disable user - O365'),
            value: Action.DISABLE_USER_O365,
          },
          {
            label: this.dependencies.translateFn('Disable user - AD'),
            value: Action.DISABLE_USER_AD,
          },
          //{ Not in Q1
          //  label: this.dependencies.translateFn('Enable O365 user'),
          //  value: Action.ENABLE_USER_O365,
          //},
          //{
          //  label: this.dependencies.translateFn('Enable AD user'),
          //  value: Action.ENABLE_USER_AD,
          //},
          {
            label: this.dependencies.translateFn('Force user credentials reset - O365'),
            value: Action.RESET_CREDENTIALS_O365,
          },
          {
            label: this.dependencies.translateFn('Mark as compromised'),
            value: Action.RISKY_USER,
          },
          {
            label: this.dependencies.translateFn('Deactivate AWS account'),
            value: Action.DEACTIVATE_USER_AWS,
          },
          {
            label: this.dependencies.translateFn('Force user credentials reset - AD'),
            value: Action.RESET_CREDENTIALS_AD,
          },
          {
            label: this.dependencies.translateFn('Manage asset'),
            value: Action.MANAGE_UNMANAGED,
          },
          //{ Not in Q1
          //  label: this.dependencies.translateFn('Block file hash'),
          //  value: Action.BLOCK_FILE_HASH,
          //},
          //{
          //  label: this.dependencies.translateFn('System repair'),
          //  value: Action.SYSTEM_REPAIR,
          //},
          //{
          //  label: this.dependencies.translateFn('Block IP'),
          //  value: Action.BLOCK_IP,
          //},
          {
            label: this.dependencies.translateFn('Remove from isolation'),
            value: Action.RESTORE_ENDPOINT,
          },
          {
            label: this.dependencies.translateFn('Delete email - GCP'),
            value: Action.DELETE_EMAIL_GOOGLE,
          },
          {
            label: this.dependencies.translateFn('Disable user - Google Workspace'),
            value: Action.DISABLE_USER_GOOGLE,
          },
          //{ For later
          //  label: this.dependencies.translateFn('Enable user - Google'),
          //  value: Action.ENABLE_USER_GOOGLE,
          //},
          {
            label: this.dependencies.translateFn('Disable user - Atlassian'),
            value: Action.DISABLE_USER_ATLASSIAN,
          },
          {
            label: this.dependencies.translateFn('Force user credentials reset - Atlassian'),
            value: Action.RESET_CREDENTIALS_ATLASSIAN,
          },
          {
            label: this.dependencies.translateFn('Force user credentials reset - Google'),
            value: Action.RESET_CREDENTIALS_GOOGLE,
          },
        ],
        removable: false,
        flexGrow: 1,
      },
      {
        column: Column.OBJECT,
        key: ColumnKeys.OBJECT,
        columnName: this.dependencies.translateFn('Object'),
        type: ColumnType.STRING,
        removable: false,
        flexGrow: 1,
      },
      {
        column: Column.TARGET,
        key: ColumnKeys.TARGET,
        columnName: this.dependencies.translateFn('Target'),
        type: ColumnType.STRING,
        removable: false,
        flexGrow: 1,
      },
      {
        column: Column.DETAILS,
        filteringDisabled: true,
        key: ColumnKeys.DETAILS,
        columnName: this.dependencies.translateFn('Details'),
        type: ColumnType.STRING,
        removable: true,
        flexGrow: 2,
      },
      {
        column: Column.EXECUTED_ON,
        key: ColumnKeys.EXECUTED_ON,
        columnName: this.dependencies.translateFn('Executed on'),
        type: ColumnType.DATE,
        removable: true,
        flexGrow: 1,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
            absolute: {
              toTimestamp: new Date().getTime(),
              // since the response grid didn't exist in 2021 this
              // filter will have no effect when first added
              // which is desired
              fromTimestamp: new Date('2021').getTime(),
            },
          },
        },
      },
      {
        column: Column.EXECUTED_BY,
        key: ColumnKeys.EXECUTED_BY,
        columnName: this.dependencies.translateFn('Executed by'),
        type: ColumnType.STRING,
        removable: true,
        flexGrow: 1,
      },
      {
        column: Column.SOURCE,
        key: ColumnKeys.SOURCE,
        columnName: this.dependencies.translateFn('Source'),
        type: ColumnType.ENUM,
        values: [
          {
            label: this.dependencies.translateFn('Graph'),
            value: Source.GRAPH,
          },
          {
            label: this.dependencies.translateFn('Recommendation'),
            value: Source.RECOMMENDATION,
          },
        ],
        removable: true,
        flexGrow: 1,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Status'),
        type: ColumnType.ENUM,
        removable: false,
        values: [ //used in filtering
          {
            label: this.dependencies.translateFn('Action needed'),
            value: Status.TO_BE_EXECUTED,
          },
          {
            label: this.dependencies.translateFn('Pending'),
            value: Status.PENDING,
          },
          {
            label: this.dependencies.translateFn('In progress'),
            value: Status.IN_EXECUTION,
          },
          {
            label: this.dependencies.translateFn('Successful'),
            value: Status.EXECUTION_SUCCESS,
          },
          {
            label: this.dependencies.translateFn('Failed'),
            value: Status.EXECUTION_FAILED,
          },
          {
            label: this.dependencies.translateFn('Dismissed'),
            value: Status.DISMISSED,
          },
          {
            label: this.dependencies.translateFn('External action needed'),
            value: Status.EXTERNAL_ACTION_NEEDED,
          },
          {
            label: this.dependencies.translateFn('Marked as done'),
            value: Status.DONE_EXTERNALLY,
          },
        ],
        flexGrow: 1,
      },
      {
        column: Column.USER_INLINE_ACTIONS,
        key: ColumnKeys.USER_INLINE_ACTIONS,
        columnName: this.dependencies.translateFn(''),
        type: ColumnType.ACTION,
        // TODO implement values
        removable: false,
        width: '50px',
      },
    ];
  }

  async getDataForView(): Promise<ResponseGridWebComponentSchema> {
    const baseColumns = [
      Column.TYPE,
      Column.ACTION,
      Column.OBJECT,
      Column.TARGET,
    ];
    const actionsNeededColumns = [
      ...baseColumns,
      Column.DETAILS,
      Column.STATUS,
      Column.USER_INLINE_ACTIONS,
    ];
    const inProgressColumns = [
      ...baseColumns,
      Column.STATUS,
      Column.EXECUTED_ON,
      Column.EXECUTED_BY,
      Column.SOURCE,
      Column.USER_INLINE_ACTIONS,
    ];
    const completedColumns = [
      ...baseColumns,
      Column.EXECUTED_ON,
      Column.EXECUTED_BY,
      Column.SOURCE,
      Column.STATUS,
      Column.USER_INLINE_ACTIONS,
    ];
    const dismissedColumns = [
      ...baseColumns,
      Column.DETAILS,
      Column.SOURCE,
      Column.STATUS,
      Column.USER_INLINE_ACTIONS,
    ];
    const allActionsColumns = [
      ...baseColumns,
      Column.DETAILS,
      Column.STATUS,
      Column.SOURCE,
      Column.USER_INLINE_ACTIONS,
    ];

    const actionsNeededStatuses = [
      Status.TO_BE_EXECUTED,
      Status.EXECUTION_FAILED,
      Status.EXTERNAL_ACTION_NEEDED,
    ];

    const completedStatuses = [Status.EXECUTION_SUCCESS, Status.DONE_EXTERNALLY];

    const dismissedStatutes = [Status.DISMISSED];
    const inProgressStatuses = [Status.PENDING, Status.IN_EXECUTION];

    const commonDefaults = {
      selectedTargets: [],
      pageSize: 100,
      compactView: false,
    };

    // For filters of a certain column to be displayed by default they have to show up in the filters array
    // with some empty values (we also have to make sure these don't affect the data fetched from the backend)
    const commonFilters = [
      // This { in: [] } tells gz-grid-filters that the filter should be shown but not actually do anything
      { [ColumnKeys.ACTION]: { in: [] } },
      { [ColumnKeys.OBJECT]: { contains: '' } },
      { [ColumnKeys.TARGET]: { contains: '' } },
    ];

    const actionsNeeded = {
      ...commonDefaults,
      id: GridViews.ACTIONS_NEEDED,
      name: this.dependencies.translateFn('Action needed'),
      type: ViewType.DEFAULT,
      filters: [
        ...commonFilters,
        { [ColumnKeys.STATUS]: { in: actionsNeededStatuses } },
        { [ColumnKeys.TYPE]: { in: [] } },
      ],
      displayedColumns: actionsNeededColumns,
    };
    const inProgress = {
      ...commonDefaults,
      id: GridViews.IN_PROGRESS,
      name: this.dependencies.translateFn('In Progress'),
      type: ViewType.DEFAULT,
      filters: [
        ...commonFilters,
        { [ColumnKeys.STATUS]: { in: inProgressStatuses } },
      ],
      displayedColumns: inProgressColumns,
    };
    const completed = {
      ...commonDefaults,
      id: GridViews.EXECUTED,
      name: this.dependencies.translateFn('Completed'),
      type: ViewType.DEFAULT,
      filters: [
        ...commonFilters,
        { [ColumnKeys.STATUS]: { in: completedStatuses } },
        { [ColumnKeys.EXECUTED_BY]: { contains: '' } },
        { [ColumnKeys.SOURCE]: { in: '' } },
      ],
      displayedColumns: completedColumns,
    };

    const dismissed = {
      ...commonDefaults,
      id: GridViews.DISMISSED,
      name: this.dependencies.translateFn('Dismissed'),
      type: ViewType.DEFAULT,
      filters: [
        ...commonFilters,
        { [ColumnKeys.STATUS]: { in: dismissedStatutes } },
        { [ColumnKeys.TYPE]: { in: [] } },
      ],
      displayedColumns: dismissedColumns,
    };

    const all = {
      ...commonDefaults,
      id: GridViews.ALL,
      name: this.dependencies.translateFn('All actions'),
      type: ViewType.DEFAULT,
      filters: [
        ...commonFilters,
        { [ColumnKeys.STATUS]: { in: [] } },
      ],
      displayedColumns: allActionsColumns,
    };

    return {
      views: [actionsNeeded, inProgress, completed, dismissed, all],
      allColumns: this.getGridColumns(),
      defaultViewProps: actionsNeeded,
      predefinedFilterKeys: [ColumnKeys.ACTION, ColumnKeys.OBJECT, ColumnKeys.TARGET, ColumnKeys.STATUS],
    };
  }
}
